const MAX_IMAGE_URL_LENGTH = 4096
const MAX_IDENTIFIER_LENGTH = 128
const ALLOWED_IMAGE_HOSTS = new Set(['cdn.mamamiya.store', 'cdn.shopify.com'])
const ULID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/i
const IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]+$/
// H-131: ?source={images.id} — images-table rows are INTEGER keyed.
const SOURCE_ID_PATTERN = /^[0-9]+$/
// H-131: ?entry=sources — open create on the source-picker act.
const ENTRY_VALUES = new Set(['sources'])
function readParams(input) {
  if (input instanceof URLSearchParams) return input
  if (input instanceof URL) return input.searchParams
  // H-140c: window.location is a Location object, not a URL instance, so the
  // instanceof branch above never matches it. Duck-typing covers URL, Location,
  // and any object exposing searchParams.
  if (input && typeof input.searchParams === 'object' && input.searchParams !== null) return input.searchParams
  // H-140c: the Location interface exposes the query string via `search`, NOT
  // `searchParams` (per the HTML spec, Location has no searchParams property).
  // This branch is what actually matches window.location in real browsers.
  if (input && typeof input.search === 'string') return new URLSearchParams(input.search)
  if (typeof input === 'string') return new URLSearchParams(input.startsWith('?') ? input : `?${input}`)
  return new URLSearchParams()
}

function one(params, name) {
  const values = params.getAll(name)
  return values.length === 1 ? values[0] : null
}

function normalizeImage(value) {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_IMAGE_URL_LENGTH) return null
  if ([...value].some((character) => character < ' ' || character === '\u007f')) return null

  let url
  try {
    url = new URL(value)
  } catch {
    return null
  }
  if (url.protocol !== 'https:' || url.username || url.password || !ALLOWED_IMAGE_HOSTS.has(url.hostname.toLowerCase())) return null

  if (url.hostname === 'cdn.mamamiya.store' && url.searchParams.has('w')) {
    url.searchParams.set('w', '4000')
  }
  return url.href
}

// H-167: exported so the PDP CTA resolver (utils/artwork-source-cta.js) shares
// the exact whitelist the create page enforces on ?source= deep links.
export { normalizeImage }

function normalizeIdentifier(value, pattern = IDENTIFIER_PATTERN) {
  if (typeof value !== 'string' || value.length === 0 || value.length > MAX_IDENTIFIER_LENGTH) return null
  if (!pattern.test(value)) return null
  return value
}

/**
 * Parse and validate the URL-only creator entry contract.
 *
 * H-140 design-entry params (action-pipeline state contract):
 *   design_material = images-table row id — Design opens with that material
 *                     located + preselected at the head of the Arts panel
 *                     (locate/preselect only; never auto-injects the canvas).
 *   design_blank    = base-product sds_id — Design preselects that blank
 *                     (same store path as the legacy ?product= deep-link).
 * Either implies mode 'design' when ?mode= is absent.
 *   ref             = attribution token (gallery / gallery_design / products /
 *                     create) — written to the mms_ref cookie at boot, mirroring
 *                     the storefront mms-share-ref.liquid contract.
 *
 * @param {URLSearchParams|string|URL} input - Query parameters to parse.
 * @returns {{injectImage: string|null, product: string|null, designId: string|null, source: string|null, sourceUrl: string|null, sourceRejected: boolean, entry: string|null, designMaterial: string|null, designBlank: string|null, ref: string|null, mode: string|null, pageUrl: string, pageTitle: string}}
 */
export function parseCreateEntryParams(input) {
  let params
  try {
    params = readParams(input)
  } catch {
    params = new URLSearchParams()
  }

  const injectImage = normalizeImage(one(params, 'injectImage'))
  const product = normalizeIdentifier(one(params, 'product'))
  const designId = normalizeIdentifier(one(params, 'design_id'), ULID_PATTERN)
  // H-131 multi-entry create funnel:
  //   source = images-table row id — create skips the hero act, straight to
  //            the gallery reveal with the row as the source image.
  //   entry  = 'sources' — create opens on the hero act with the four-pipe
  //            source picker already open (storefront "use your own image").
  // H-167: source ALSO accepts a whitelisted artwork URL (PDP "MAKE IT MINE"
  // deep link, custom.artwork_source metafield). Numeric values stay on the
  // H-131 images-id path; anything else present-but-invalid is flagged
  // (sourceRejected) so the entry layer can refuse with a toast instead of
  // silently dropping the funnel.
  const rawSource = one(params, 'source')
  const source = normalizeIdentifier(rawSource, SOURCE_ID_PATTERN)
  const sourceUrl = source ? null : normalizeImage(rawSource)
  const sourceRejected = !source && !sourceUrl && typeof rawSource === 'string' && rawSource.length > 0
  const entryParam = one(params, 'entry')
  const entry = entryParam && ENTRY_VALUES.has(entryParam) ? entryParam : null
  // H-140 design entry: material id is an images-table row (integer keyed,
  // same shape as source); blank is a base-product sds_id (opaque identifier).
  const designMaterial = normalizeIdentifier(one(params, 'design_material'), SOURCE_ID_PATTERN)
  const designBlank = normalizeIdentifier(one(params, 'design_blank'))
  // H-140 ref attribution token — same shape as the storefront mms-share-ref
  // writer accepts (opaque [A-Za-z0-9_-] token, length-capped).
  const ref = normalizeIdentifier(one(params, 'ref'))
  const modeParam = one(params, 'mode')
  const mode = designId
    ? 'design'
    : modeParam === 'design' || modeParam === 'create'
      ? modeParam
      : designMaterial || designBlank
        ? 'design'
        // H-167 v2: a rejected deep link must still activate create mode,
        // otherwise the refusal toast in studio/entry.js never fires.
        : injectImage || source || sourceUrl || sourceRejected || entry
          ? 'create'
          : null

  return {
    injectImage,
    product,
    designId,
    source,
    sourceUrl,
    sourceRejected,
    entry,
    designMaterial,
    designBlank,
    ref,
    mode,
    pageUrl: normalizeImage(one(params, 'pageUrl')) || '',
    pageTitle: (() => {
      const value = one(params, 'pageTitle')
      return typeof value === 'string' && value.length <= MAX_IDENTIFIER_LENGTH && !/[\u0000-\u001f\u007f]/.test(value) ? value : ''
    })(),
    error: null,
  }
}

export default parseCreateEntryParams
